import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import WizardCst from "@components/WizardCst";
import { WizardStep } from "@components/WizardCst/types";
import { UserWizardData } from "./types";
import { validateMainInfo } from "./validation";
import MainInfoStep from "./steps/MainInfoStep";
import AdditionalInfoStep from "./steps/AdditionalInfoStep";
import AddToGroupsStep from "./steps/AddToGroupsStep";

interface UserWizardProps {
  mode: "create" | "edit";
  initialData: UserWizardData;
  backTo: string;
  onComplete: (data: UserWizardData) => void | Promise<void>;
  isCompleting?: boolean;
}

const UserWizard: React.FC<UserWizardProps> = ({
  mode,
  initialData,
  backTo,
  onComplete,
  isCompleting,
}) => {
  const { t } = useTranslation("user-wizard-v2");

  const steps = useMemo<WizardStep<UserWizardData>[]>(
    () => [
      {
        key: "main-info",
        label: t("steps.mainInfo"),
        Component: MainInfoStep,
        validate: validateMainInfo,
      },
      {
        key: "additional-info",
        label: t("steps.additionalInfo"),
        Component: AdditionalInfoStep,
      },
      {
        key: "groups",
        label: t("steps.groups"),
        Component: AddToGroupsStep,
      },
    ],
    [t],
  );

  return (
    <AnimationFadeIn>
      <Box sx={{ py: 3, pr: 3 }}>
        <WizardCst<UserWizardData>
          title={mode === "edit" ? t("titleEdit") : t("titleCreate")}
          breadcrumb={<Breadcrumb />}
          backLink={{ label: t("backLink"), to: backTo }}
          steps={steps}
          initialData={initialData}
          onComplete={onComplete}
          abandonMode={mode}
          abandonCopy={{
            title: String(
              t(
                mode === "edit" ? "abandon.edit-title" : "abandon.create-title",
              ),
            ),
          }}
          nextLabel={t("next")}
          completeLabel={t("confirm")}
          backLabel={t("back")}
          resetLabel={t("reset")}
          isCompleting={isCompleting}
          resetConfirm={{
            title: t("resetConfirm.title"),
            description: t("resetConfirm.description"),
            confirmLabel: t("resetConfirm.confirm"),
            cancelLabel: t("resetConfirm.cancel"),
          }}
        />
      </Box>
    </AnimationFadeIn>
  );
};

export default UserWizard;
